import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser, useClerk } from '@clerk/clerk-react';
import { Settings, LogOut, Users, FileCheck, ChevronDown, Loader2 } from 'lucide-react';
import { useAuthContext } from '@/hooks/useAuthContext';

export const UserProfileMenu: React.FC = () => {
  const { user } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const { orgRole, orgTier } = useAuthContext();

  const [isOpen, setIsOpen] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSignOut = async () => {
    setIsSigningOut(true);
    try {
      await signOut({ redirectUrl: '/' });
    } catch {
      navigate('/sign-in');
    } finally {
      setIsSigningOut(false);
    }
  };

  const initials = (user?.fullName || user?.firstName || 'Partner')
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const primaryEmail = user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || '';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-1 rounded-xl hover:bg-zinc-800/60 transition cursor-pointer group focus:outline-none"
        aria-label="User Profile Menu"
        aria-expanded={isOpen}
      >
        {user?.imageUrl ? (
          <img
            src={user.imageUrl}
            alt={user.fullName || 'User'}
            className="w-8 h-8 rounded-lg object-cover ring-2 ring-zinc-700/60 group-hover:ring-indigo-500 transition"
          />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-linear-to-tr from-indigo-600 to-violet-500 flex items-center justify-center font-bold text-white text-xs shadow-md shadow-indigo-500/20 ring-2 ring-zinc-700/60 group-hover:ring-indigo-500 transition">
            {initials}
          </div>
        )}
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 group-hover:text-zinc-200 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Custom White-Label Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-[#0E1015] border border-zinc-800 rounded-2xl shadow-2xl py-2 z-50 backdrop-blur-xl animate-in fade-in zoom-in-95 duration-150">
          {/* User Header */}
          <div className="px-4 py-3 border-b border-zinc-800/80">
            <p className="text-sm font-semibold text-white truncate">
              {user?.fullName || 'Partner Account'}
            </p>
            <p className="text-xs text-zinc-400 truncate mt-0.5">
              {primaryEmail}
            </p>
            <div className="mt-2 flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase px-2 py-0.5 bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 rounded-md">
                {orgTier || 'Referral'} Tier
              </span>
              <span className="text-[10px] font-mono text-zinc-400 capitalize">
                {orgRole || 'Member'}
              </span>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                navigate('/settings/profile');
              }}
              className="w-full px-4 py-2 text-left text-xs text-zinc-300 hover:text-white hover:bg-zinc-800/60 flex items-center gap-2.5 transition cursor-pointer"
            >
              <Settings className="w-4 h-4 text-zinc-400" />
              <span>Profile & Settings</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                navigate('/settings/team');
              }}
              className="w-full px-4 py-2 text-left text-xs text-zinc-300 hover:text-white hover:bg-zinc-800/60 flex items-center gap-2.5 transition cursor-pointer"
            >
              <Users className="w-4 h-4 text-zinc-400" />
              <span>Team Access</span>
            </button>

            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                navigate('/settings/kyc');
              }}
              className="w-full px-4 py-2 text-left text-xs text-zinc-300 hover:text-white hover:bg-zinc-800/60 flex items-center gap-2.5 transition cursor-pointer"
            >
              <FileCheck className="w-4 h-4 text-zinc-400" />
              <span>KYC Verification</span>
            </button>
          </div>

          {/* Sign Out */}
          <div className="pt-1 border-t border-zinc-800/80">
            <button
              type="button"
              disabled={isSigningOut}
              onClick={handleSignOut}
              className="w-full px-4 py-2 text-left text-xs text-rose-400 hover:text-rose-300 hover:bg-rose-500/10 flex items-center gap-2.5 transition cursor-pointer disabled:opacity-50"
            >
              {isSigningOut ? (
                <Loader2 className="w-4 h-4 animate-spin text-rose-400" />
              ) : (
                <LogOut className="w-4 h-4 text-rose-400" />
              )}
              <span>{isSigningOut ? 'Signing out...' : 'Sign Out'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
